import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Card, Spin, Row, Col, Statistic, Button, Modal, Form, Input, InputNumber, Popconfirm, Space, message, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, MinusCircleOutlined, TagOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { yuan } from '../utils/format.js';
import { apiFetch, notifyDataChanged, onDataChanged } from '../utils/api.js';
import { prodCode as toParentCode } from '../utils/prodCode.js';

// 单价/辅材这类小数值展示到 4 位小数
const fmt4 = (v) => (v == null || isNaN(v) ? '0.0000' : Number(v).toFixed(4).replace(/0+$/, '').replace(/\.$/, '') || '0');

const emptyCodeRow = () => ({ qty: null, product_cost: null, weight: null, packaging_tag: null });

export default function Products() {
  const [data, setData] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null); // null=关闭, {} = 新增(多编码), {record} = 编辑(单编码)
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm();
  const [tagsForm] = Form.useForm();
  const [packagingTags, setPackagingTags] = useState([]); // 辅材标签列表
  const [tagsModal, setTagsModal] = useState(false); // 辅材标签管理弹窗
  // 角色：管理员显示财务汇总（总营收/总利润/毛利率），仓库等角色隐藏
  const role = (() => { try { return JSON.parse(localStorage.getItem('admin_user') || '{}').role || 'other'; } catch { return 'other'; } })();
  const isAdmin = role === 'admin';
  // 实时监听上级编码，用于下级数量框的前缀预览（如 HB-11003-）
  const watchProdCode = Form.useWatch('prod_code', form);
  // 下级数量框前缀：锁定模式（给已有产品加规格）读 editModal.prodCode，普通新增读表单 watchProdCode
  const prodPrefix = editModal?.lockProd ? editModal.prodCode : watchProdCode;

  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    apiFetch('/api/products')
      .then(r => r.json())
      .then(d => { setData(d.data); if (!silent) setLoading(false); })
      .catch(() => { if (!silent) setLoading(false); });
    // 权威产品编码表（分组依据）
    apiFetch('/api/products/catalog').then(r => r.json()).then(d => setCatalog(d.data || [])).catch(() => {});
  }, []);

  const loadTags = useCallback(() => {
    apiFetch('/api/packaging-tags').then(r => r.json()).then(d => setPackagingTags(d.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadData(); loadTags(); }, [loadData, loadTags]);
  // 数据变更（订单改编码/导入等）→ 静默刷新（不闪 loading）
  useEffect(() => onDataChanged(() => { loadData(true); }), [loadData]);

  // 树形数据：按权威产品编码表分组（产品编码 + 名称），规格挂产品下
  const treeData = useMemo(() => {
    if (catalog.length) {
      // 产品表优先：每组 = 产品，子行 = 规格
      return catalog.map((p) => ({
        key: 'p:' + p.code,
        product_name: p.name,
        prod_code: p.code,
        isGroup: true,
        children: (p.specs || []).map((sp, i) => {
          const master = data.find(x => String(x.spec_code) === String(sp)) || { spec_code: sp, product_name: p.name, product_cost: null, weight: null, packaging_tag: null };
          return { ...master, key: 'c:' + sp + '#' + i };
        }),
      })).sort((a, b) => (b.children[0] && b.children[0].total_revenue || 0) - (a.children[0] && a.children[0].total_revenue || 0));
    }
    // 兜底：按名称分组
    const map = {};
    for (const p of data) {
      const name = p.product_name || '未命名';
      (map[name] = map[name] || []).push(p);
    }
    return Object.entries(map)
      .map(([name, list]) => ({
        key: 'p:' + name,
        product_name: name,
        // 兜底分支：catalog 没返数据时，从首个子项规格编码推导上级编码（如 HB-11003-500 → HB-11003），否则「新增编码」会拿不到 prod_code
        prod_code: toParentCode(list[0]?.spec_code || ''),
        isGroup: true,
        children: list.map((p, i) => ({ ...p, key: 'c:' + p.spec_code + '#' + i })),
      }))
      .sort((a, b) => (b.children[0].total_revenue || 0) - (a.children[0].total_revenue || 0));
  }, [data]);

  // 搜索过滤：编码前缀 或 商品名称关键词（组名匹配保留整组；编码前缀匹配只保留匹配行）
  const filteredTree = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return treeData;
    return treeData
      .map(g => {
        const children = g.children.filter(c => String(c.spec_code || '').startsWith(kw));
        if (children.length) return { ...g, children };
        if (String(g.product_name || '').includes(kw)) return g; // 名称关键词匹配 → 整组
        return null;
      })
      .filter(Boolean);
  }, [treeData, keyword]);

  // 新增：product 为 {prod_code, product_name} 时 = 给已有产品加规格（锁定上级），null = 全新产品
  const openAdd = (product) => {
    form.resetFields();
    // 锁定模式（给已有产品加规格）下，上级编码/名称直接存进 editModal，绕开「Form.Item 包 disabled Input 时 setFieldsValue 不显示」的时序坑
    setEditModal({
      isNew: true,
      lockProd: !!product,
      prodCode: product?.prod_code || '',
      productName: product?.product_name || '',
    });
    // codes 仍走 Form.List
    form.setFieldsValue({ codes: [emptyCodeRow()] });
  };
  const openEdit = (record) => {
    form.resetFields();
    form.setFieldsValue(record);
    setEditModal(record);
  };

  // 保存辅材标签（增删改）→ 后端重算全部订单辅材
  const saveTags = async () => {
    const values = await tagsForm.validateFields();
    const tags = (values.tags || []).map(t => ({ name: String(t.name || '').trim(), price: Number(t.price) || 0 }));
    const res = await apiFetch('/api/packaging-tags', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags }),
    }).then(r => r.json());
    if (res.success) {
      message.success('已保存辅材标签，订单辅材已重算');
      setTagsModal(false);
      loadTags();
      loadData(true);
      notifyDataChanged();
    } else message.error(res.error || '保存失败');
  };

  const handleSave = async () => {
    try {
      if (editModal && editModal.spec_code) {
        // 编辑：单编码
        const values = await form.validateFields();
        const clean = {};
        for (const [k, v] of Object.entries(values)) {
          if (v != null && v !== '') clean[k] = v;
        }
        const res = await apiFetch(`/api/product-master/${encodeURIComponent(editModal.spec_code)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clean),
        }).then(r => r.json());
        if (res.success) message.success('已更新商品成本');
        else { message.error(res.error || '保存失败'); return; }
      } else {
        // 新增：先录上级（产品编码+名称），再录下级（数量档），系统自动拼「上级编码-数量」
        const isLock = !!editModal?.lockProd;
        let prodCode, productName, codes;
        if (isLock) {
          // 锁定模式：上级字段从 editModal 拿（渲染时不走 Form），codes 单独校验
          prodCode = String(editModal.prodCode || '').trim();
          productName = String(editModal.productName || '').trim();
          if (!prodCode) { message.warning('上级编码为空'); return; }
          if (!productName) { message.warning('商品名称为空'); return; }
          try {
            const cv = await form.validateFields(['codes']);
            codes = (cv.codes || []).filter(c => c && c.qty != null && String(c.qty).trim() !== '');
          } catch (e) {
            if (e?.errorFields) return; // codes 校验失败（数量必填）
            throw e;
          }
        } else {
          // 普通模式：全量校验
          const values = await form.validateFields();
          prodCode = String(values.prod_code || '').trim();
          productName = String(values.product_name || '').trim();
          codes = (values.codes || []).filter(c => c && c.qty != null && String(c.qty).trim() !== '');
        }
        if (!codes.length) { message.warning('请至少填写一个数量档'); return; }
        let ok = 0; let firstErr = '';
        const seen = new Set();
        for (const c of codes) {
          const qty = Number(c.qty);
          if (!Number.isInteger(qty) || qty <= 0) { if (!firstErr) firstErr = '数量必须为正整数'; continue; }
          const spec_code = `${prodCode}-${qty}`;
          if (seen.has(spec_code)) { if (!firstErr) firstErr = `数量档 ${qty} 重复`; continue; }
          seen.add(spec_code);
          const payload = {
            product_name: productName,
            spec_code,
            product_cost: c.product_cost ?? 0,
            weight: c.weight ?? 0,
            packaging_tag: c.packaging_tag || '', // 引用辅材标签
          };
          const res = await apiFetch('/api/product-master', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).then(r => r.json());
          if (res.success) ok++;
          else if (!firstErr) firstErr = res.error || '新增失败';
        }
        if (ok > 0) message.success(`已新增 ${ok} 个规格编码${firstErr ? `（${firstErr}）` : ''}`);
        else { message.error(firstErr || '新增失败'); return; }
      }
      setEditModal(null);
      loadData(true); // 静默更新（页面不刷新闪烁）
      notifyDataChanged(); // 通知其它页面（订单成本/统计）同步
    } catch (e) {
      if (e?.errorFields) return;
      message.error('保存失败，请重试');
    }
  };

  const handleDelete = async (spec_code) => {
    const res = await apiFetch(`/api/product-master/${encodeURIComponent(spec_code)}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadData(true); notifyDataChanged(); }
    else message.error(res.error || '删除失败');
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" /></div>;

  const totalRevenue = data.reduce((a, b) => a + (b.total_revenue || 0), 0);
  const totalProfit = data.reduce((a, b) => a + (b.total_profit || 0), 0);
  const totalQty = data.reduce((a, b) => a + (b.total_quantity || 0), 0);
  const productCount = treeData.length;

  const columns = [
    {
      title: '上级编码', key: 'prod_code', width: 160,
      render: (_, r) => r.isGroup
        ? <b style={{ fontFamily: 'monospace' }}>{r.prod_code || toParentCode(r.children && r.children[0] ? r.children[0].spec_code : '')}</b>
        : <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.spec_code}</span>,
    },
    {
      title: '商品名称', key: 'name', width: 220,
      render: (_, r) => r.isGroup
        ? <span style={{ fontWeight: 600 }}>{r.product_name} <span style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 12 }}>（{r.children.length} 个编码）</span></span>
        : <span style={{ fontSize: 12 }}>{r.product_name || ''}</span>,
    },
    { title: '单价(元/件)', dataIndex: 'product_cost', width: 100, align: 'right',
      render: (v, r) => r.isGroup ? '' : <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{fmt4(v)}</span> },
    { title: '重量（克）', dataIndex: 'weight', width: 80, align: 'right', render: (v, r) => r.isGroup ? '' : (v || 0).toLocaleString() },
    { title: '辅材标签', dataIndex: 'packaging_tag', width: 110, align: 'center',
      render: (v, r) => r.isGroup ? '' : (v ? <Tag color="#378ADD">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>无</span>) },
    { title: '订单数', dataIndex: 'order_count', width: 70, align: 'right', render: (v, r) => r.isGroup ? '' : v },
    { title: '总营收', dataIndex: 'total_revenue', width: 105, align: 'right',
      render: (v, r) => r.isGroup
        ? <span style={{ color: '#D85A30', fontWeight: 600 }}>¥{yuan(r.children.reduce((a, c) => a + (c.total_revenue || 0), 0))}</span>
        : <span style={{ color: '#D85A30' }}>¥{yuan(v)}</span> },
    { title: '总利润', dataIndex: 'total_profit', width: 105, align: 'right',
      render: (v, r) => r.isGroup
        ? <span style={{ color: '#CF1322', fontWeight: 600 }}>¥{yuan(r.children.reduce((a, c) => a + (c.total_profit || 0), 0))}</span>
        : <span style={{ color: '#CF1322' }}>¥{yuan(v)}</span> },
    { title: '毛利率', key: 'margin', width: 90, align: 'right',
      render: (_, r) => {
        if (r.isGroup) {
          const rev = r.children.reduce((a, c) => a + (c.total_revenue || 0), 0);
          const pf = r.children.reduce((a, c) => a + (c.total_profit || 0), 0);
          const m = rev > 0 ? pf / rev : 0;
          return <span style={{ color: m >= 0.15 ? '#CF1322' : m >= 0.08 ? '#EF9F27' : '#3F8600' }}>{(m * 100).toFixed(3)}%</span>;
        }
        const m = r.total_revenue > 0 ? r.total_profit / r.total_revenue : 0;
        return <span style={{ color: m >= 0.15 ? '#CF1322' : m >= 0.08 ? '#EF9F27' : '#3F8600' }}>{(m * 100).toFixed(3)}%</span>;
      } },
    {
      title: '操作', key: 'op', width: 150, fixed: 'right',
      render: (_, r) => r.isGroup ? (
        <Button size="small" icon={<PlusOutlined />} onClick={() => openAdd({ prod_code: r.prod_code, product_name: r.product_name })}>新增编码</Button>
      ) : (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title={`确认删除编码 ${r.spec_code}？`} onConfirm={() => handleDelete(r.spec_code)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(c => isAdmin || !['总营收', '总利润', '毛利率'].includes(c.title));

  const top10 = [...data].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0)).slice(0, 10);
  const barOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['营收', '利润'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'value', name: '元' },
    yAxis: { type: 'category', data: top10.map(p => p.product_name).reverse(), axisLabel: { fontSize: 11 } },
    series: [
      { name: '营收', type: 'bar', itemStyle: { color: '#D85A30' }, data: top10.map(p => p.total_revenue || 0).reverse() },
      { name: '利润', type: 'bar', itemStyle: { color: '#CF1322' }, data: top10.map(p => p.total_profit || 0).reverse() },
    ],
  };

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={isAdmin ? 6 : 12}>
          <div className="stat-card" style={{ '--sc': '#D85A30', padding: '16px 18px' }}>
            <div className="stat-label">商品款数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{productCount}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>款</span></div>
          </div>
        </Col>
        <Col span={isAdmin ? 6 : 12}>
          <div className="stat-card" style={{ '--sc': '#378ADD', padding: '16px 18px' }}>
            <div className="stat-label">规格编码数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{data.length}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>个</span></div>
          </div>
        </Col>
        {isAdmin && <Col span={6}>
          <div className="stat-card" style={{ '--sc': '#CF1322', padding: '16px 18px' }}>
            <div className="stat-label">总营收</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#CF1322' }}>¥{yuan(totalRevenue)}</div>
          </div>
        </Col>}
        {isAdmin && <Col span={6}>
          <div className="stat-card" style={{ '--sc': '#993C1D', padding: '16px 18px' }}>
            <div className="stat-label">总利润</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#993C1D' }}>¥{yuan(totalProfit)}</div>
          </div>
        </Col>}
      </Row>

      <Card
        title="商品成本管理（一款商品可登记多个规格编码）"
        extra={
          <Space wrap>
            <Input.Search
              placeholder="编码前缀 / 商品名称关键词"
              allowClear
              style={{ width: 240 }}
              onChange={e => setKeyword(e.target.value)}
              title="按规格编码前缀（如 HB-11003）或商品名称关键词（如 两面针）快速查找"
            />
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
            <Button icon={<TagOutlined />} onClick={() => { tagsForm.setFieldsValue({ tags: packagingTags }); setTagsModal(true); }}>辅材标签</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>新增商品</Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <p style={{ color: '#8c8c8c', marginTop: 0, marginBottom: 12 }}>
          一款商品（如「黑标背空小方孔两面针」）可按数量档登记<b>多个规格编码</b>（如 HB-11003-500 / HB-11003-1000 / HB-11003-2000），各编码分别填写<b>单价(元/件)、重量(克)、辅材标签</b>。订单按编码匹配成本与重量：商品成本 = 单价 × 出货数量；<b>辅材按引用标签固定一口价</b>（如 500纸箱 2.7 元/单，不随数量变化）；运费按「单件重量×数量」的总重量查「运费模板」档位；最终利润 = 收入 − 成本 − 运费 − 辅材。
        </p>
        <Table
          columns={columns}
          dataSource={filteredTree}
          rowKey="key"
          size="small"
          scroll={{ x: 1250 }}
          pagination={{ pageSize: 30, showTotal: t => `共 ${t} 个商品` }}
          expandable={{ defaultExpandAllRows: false }}
        />
      </Card>

      <Card title="商品营收与利润对比前十">
        <ReactECharts option={barOption} theme="brand" style={{ height: 360 }} />
      </Card>

      <Modal
        title={editModal?.spec_code ? `编辑编码 ${editModal.spec_code}` : (editModal?.lockProd ? '新增规格编码（下级）' : '新增商品（先录上级，再录下级）')}
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        width={720}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {editModal?.spec_code ? (
            // ===== 编辑单个规格编码（下级）=====
            <>
              <Form.Item label="上级编码（产品编码）" extra={<span style={{ color: '#8c8c8c' }}>由规格编码自动推导，只读</span>}>
                <Input value={toParentCode(editModal.spec_code)} disabled />
              </Form.Item>
              <Form.Item name="spec_code" label="规格编码(款号)">
                <Input disabled />
              </Form.Item>
              <Form.Item name="product_name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}>
                <Input placeholder="如 黑标背空小方孔两面针" disabled />
              </Form.Item>
              <Form.Item name="product_cost" label="单价(元/件)" rules={[{ required: true, message: '请输入单价' }]}>
                <InputNumber min={0} precision={4} style={{ width: '100%' }} placeholder="如 0.148" />
              </Form.Item>
              <Form.Item name="weight" label="重量(g)" extra={<span style={{ color: '#8c8c8c' }}>单件重量（克），订单运费按「单件重量×数量」总重量查模板档位</span>}>
                <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如 12（克）" />
              </Form.Item>
              <Form.Item name="packaging_tag" label="辅材标签" extra={<span style={{ color: '#8c8c8c' }}>引用标签固定辅材费（每单固定支出，不随数量变化）</span>}>
                <Select allowClear placeholder="无辅材" style={{ width: '100%' }}
                  options={packagingTags.map(t => ({ value: t.name, label: `${t.name}（¥${yuan(t.price)}）` }))} />
              </Form.Item>
            </>
          ) : (
            // ===== 新增：先录上级，再录下级 =====
            <>
              <div style={{ background: '#FFF7F4', border: '1px dashed #D85A30', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, color: '#D85A30', marginBottom: 10 }}>① 上级（产品）</div>
                {editModal?.lockProd ? (
                  // 锁定模式：从 editModal 读值直接展示，不走 Form（避开 disabled Input + setFieldsValue 时序坑）
                  <>
                    <Form.Item label="上级编码（产品编码）"
                      extra={<span style={{ color: '#8c8c8c' }}>库存、成本都按此编码归集，如 HB-11003、SMF-0062-混合、0001-板子</span>} style={{ marginBottom: 12 }}>
                      <Input value={editModal.prodCode} disabled />
                    </Form.Item>
                    <Form.Item label="商品名称" style={{ marginBottom: 0 }}>
                      <Input value={editModal.productName} disabled />
                    </Form.Item>
                  </>
                ) : (
                  // 普通新增：可编辑
                  <>
                    <Form.Item name="prod_code" label="上级编码（产品编码）" rules={[{ required: true, message: '请输入上级编码' }]}
                      extra={<span style={{ color: '#8c8c8c' }}>库存、成本都按此编码归集，如 HB-11003、SMF-0062-混合、0001-板子</span>} style={{ marginBottom: 12 }}>
                      <Input placeholder="如 HB-11003" />
                    </Form.Item>
                    <Form.Item name="product_name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]} style={{ marginBottom: 0 }}>
                      <Input placeholder="如 黑标背空小方孔两面针" />
                    </Form.Item>
                  </>
                )}
              </div>

              <div style={{ fontWeight: 600, color: '#D85A30', marginBottom: 10 }}>② 下级（规格编码）</div>
              <p style={{ color: '#8c8c8c', fontSize: 12, marginTop: 0, marginBottom: 12 }}>
                每个数量档填一行，系统自动生成完整规格编码「上级编码-数量」（如 HB-11003-500）。
              </p>
              <Form.List name="codes">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(field => (
                      <div key={field.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <Form.Item name={[field.name, 'qty']} rules={[{ required: true, message: '数量必填' }]} style={{ marginBottom: 0, flex: 2 }}>
                          <InputNumber min={1} precision={0} placeholder="数量，如 500" addonBefore={prodPrefix ? `${prodPrefix}-` : '上级-'} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'product_cost']} style={{ marginBottom: 0, flex: 1 }}>
                          <InputNumber min={0} precision={4} placeholder="单价(元/件)" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'weight']} style={{ marginBottom: 0, flex: 1 }}>
                          <InputNumber min={0} precision={0} placeholder="重量（克）" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'packaging_tag']} style={{ marginBottom: 0, flex: 1 }}>
                          <Select allowClear placeholder="辅材标签" style={{ width: '100%' }}
                            options={packagingTags.map(t => ({ value: t.name, label: t.name }))} />
                        </Form.Item>
                        {fields.length > 1 && (
                          <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                        )}
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add(emptyCodeRow())} icon={<PlusOutlined />} block>
                      添加一个数量档
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Form>
      </Modal>

      {/* 辅材标签管理（可增删改价格） */}
      <Modal
        title="辅材标签管理（引用标签固定辅材费，每单一口价）"
        open={tagsModal}
        onCancel={() => setTagsModal(false)}
        onOk={saveTags}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        width={520}
      >
        <Form form={tagsForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.List name="tags">
            {(fields, { add, remove }) => (
              <>
                {fields.map(field => (
                  <div key={field.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'name']} rules={[{ required: true, message: '标签名必填' }]} style={{ marginBottom: 0, flex: 2 }}>
                      <Input placeholder="辅材标签名，如 口袋" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'price']} rules={[{ required: true, message: '价格必填' }]} style={{ marginBottom: 0, flex: 1 }}>
                      <InputNumber min={0} precision={2} placeholder="价格(元)" style={{ width: '100%' }} />
                    </Form.Item>
                    <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                  </div>
                ))}
                <Button type="dashed" block onClick={() => add({ name: '', price: 0 })} icon={<PlusOutlined />}>
                  添加标签
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
